import Filter from "components/Filter";
import { Header } from "components/Header";
import ExportReportModal from "components/Modals/ExportReportModal";
import { SearchBar } from "components/SearchBar";
import React, { useEffect, useState } from "react";

import { IOccurrence } from "interfaces/Occurrence";
import { useHistoric } from "services/OccurrenceService";
import * as S from "./styles";

import { DashboardTemplate } from "templates/DashboardTemplate";

import LastCoordsAdapter from "adapters/LastCoordsAdapter";
import ActivationModal from "components/Modals/ActivationModal";
import ManualOccurrenceModal from "components/Modals/OccurrenceManagementModal";
import { useManualOccurrence } from "contexts/register/ManualOccurrence";
import { FindOptionsOrder, FindOptionsOrderValue } from "interfaces/IQuery";
import { IUser } from "interfaces/User";
import { useLocation } from "react-router-dom";
import { handleOccurrenceDeleteSubmit } from "services/handles/OccurrenceHandles";
import { CheckAlert } from "utils/checkAlert";
import OccurrenceList from "./OccurrenceList";
import { HistoricItem } from "interfaces/Historic";
import { HistoricCard } from "components/Cards/HistoricCard";

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

type DisplayMode = "list" | "cards";

export function HistoricOccurrences() {
  let query = useQuery();
  const [search, setSearch] = useState("");
  const [occurrenceId, setOccurrenceId] = useState("");
  const [status, setStatus] = useState<
    "andamento" | "aguardando" | "concluido" | undefined
  >();
  const [exportModal, setExportModal] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [storageUser, setStorageUser] = useState<IUser>();
  const [activationModal, setActivationModal] = useState(false);
  const [displayMode, setDisplayMode] = useState<DisplayMode>("cards");
  const [order, setOrder] = useState<FindOptionsOrder<IOccurrence>>();
  const [isOccurrenceManagementModalOpen, setIsOccurrenceManagementModalOpen] =
    useState<boolean>(false);
  const [filterByAttendant, setFilterByAttendant] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string>("all");
  const [page, setPage] = useState<number>(1);


  const filters =[ 
    'protocol',
    'clientDocument',
    'plate',
    'clientName',
    'concatComments'
  ]

  const {data} = useHistoric({
    filter: {
      page,
      size: 12,
      params: search.length > 1 ? 
        filters.map((key) => ({
          patrimonialType: search === "1" ? 1 : search === "2" ? 2 : undefined,
          active: true,
          status,
          attendantId: filterByAttendant ? "fakeId" : undefined,
          [key]: `%${search}%`
        }) )
      :[
        {
          active: true,
          status,
          attendantId: filterByAttendant ? "fakeId" : undefined
        },
      ],
      order: order,
  }})

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      setStorageUser(JSON.parse(user));
    }
    CheckAlert();
  }, []);

  useEffect(() => {
    const _filter = query.get("type");
    if (_filter) {
      switch (_filter) {
        case "all":
          setStatus(undefined);
          setSelectedTab("all");
          break;
        case "inProgress":
          setStatus("andamento");
          setSelectedTab("andamento");
          break;
        case "waiting":
          setStatus("aguardando");
          setSelectedTab("aguardando");
          break;
        case "finished":
          setStatus("concluido");
          setSelectedTab("concluido");
          break;
        default:
          setStatus(undefined);
          break;
      }
    }
  }, []);

  const { setOccurrenceToEditId, occurrenceType } = useManualOccurrence();

  return (
    <S.Container>
      <DashboardTemplate
        containerClass="wrapper"
        page={page}
        setPage={setPage}
        total={data?.item.count?.queryCount??0}
      >
        <Header title={"Histórico de ocorrências"} />
        <Filter
          onChange={(value: FindOptionsOrderValue) => {
            setOrder({ createdAt: value });
          }}
          onClicks={[
            () => {
              setStatus(undefined);
              setSelectedTab("all");
            },
            () => {
              setStatus("andamento");
              setSelectedTab("andamento");
            },
            () => {
              setStatus("aguardando");
              setSelectedTab("aguardando");
            },
            () => {
              setStatus("concluido");
              setSelectedTab("concluido");
            },
          ]}
          order={true}
          selectedTab={selectedTab}
          filter={typeof search !== "string" ? search[0] : search}
          labels={[
            {
              // text: `Todas (${data?.item.item.all ?? 0})`,
              text: `Todas (${data?.item.count?.all ?? 0})`,
              value: "all",
            },
            {
              text: `Em andamento (${data?.item.count?.progress ?? 0})`,
              value: "andamento",
            },
            {
              text: `Aguardando atendente (${data?.item.count?.await ?? 0})`,
              value: "aguardando",
            },
            {
              text: `Finalizada (${data?.item.count?.finish ?? 0})`,
              value: "concluido",
            },
          ]}
        />
        <S.SearchBarContainer>
          <SearchBar
            type="text"
            name="filter"
            id="filter"
            placeholder="Procure por protocolo..."
            buttonText="Exportar relatorio"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearch(event.target.value);
            }}
            required={false}
            haveExport={storageUser?.role.name === "financial"}
            onSaveClick={() => {
              setExportModal(true);
            }}
            isOccurrence
            onClick={() => {
              setIsReportModalOpen(true);
            }}
          />
          <S.DisplaySelectionContainer>
            <S.ButtonsContainer>
              <S.StyledButton
                title="Cards"
                type="button"
                onClick={() => setDisplayMode("cards")}
                selected={displayMode !== "cards"}
              />
              <S.StyledButton
                title="Lista"
                type="button"
                onClick={() => setDisplayMode("list")}
                selected={displayMode !== "list"}
              />
              <S.StyledButton
                title={
                  filterByAttendant ? "Minhas ocorrencias" : "Todas ocorrencias"
                }
                type="button"
                onClick={() => setFilterByAttendant((prev) => !prev)}
              />
            </S.ButtonsContainer>
          </S.DisplaySelectionContainer>
        </S.SearchBarContainer>

        {displayMode === "cards" && (
          <>
            <S.CardsContainer>
              {data?.item.items?.map((occurrence: HistoricItem) => {
                return (
                  <HistoricCard
                    key={occurrence.id}
                    occurrence={LastCoordsAdapter.adapt(occurrence as any) as any}
                    reportClick={() => {
                      setOccurrenceToEditId(occurrence.id);
                      setIsOccurrenceManagementModalOpen(true);
                    }}
                  />
                );
              })}
            </S.CardsContainer>
          </>
        )}
        {displayMode === "list" && (
          <OccurrenceList
            occurrences={data?.item.items ?? []}
            onReportButtonClick={(occurrence) => {
              setOccurrenceId(occurrence.id);
              setOccurrenceToEditId(occurrence.id);
              setIsOccurrenceManagementModalOpen(true);
            }}
          />
        )}

        <ExportReportModal
          modalIsOpen={exportModal}
          setIsOpen={setExportModal}
        />

        <ActivationModal
          title={`Deseja cancelar a ocorrência`}
          content="Uma ocorrência cancelada não aparecerá na listagem."
          modalIsOpen={activationModal}
          setIsOpen={setActivationModal}
          buttonText="Sim, cancelar"
          onClick={() => {
            handleOccurrenceDeleteSubmit({
              id:occurrenceId,
            });
          }}
        />
      </DashboardTemplate>

      <ManualOccurrenceModal
        isOpen={isOccurrenceManagementModalOpen}
        setIsOpen={() => setIsOccurrenceManagementModalOpen(false)}
        type={occurrenceType}
      />

      {
        <ExportReportModal
          modalIsOpen={isReportModalOpen}
          setIsOpen={() => {
            setIsReportModalOpen(false);
          }}
        />
      }
    </S.Container>
  );
}
